import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";

const Tooltip = ({ content, description, isCustom = false, position = 'right' }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [tooltipStyle, setTooltipStyle] = useState({
    position: 'fixed',
    zIndex: 9999,
    opacity: 0,
    transition: 'opacity 0.2s ease-in-out',
    visibility: 'hidden'
  });
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);
  
  const size = description?.length > 15 ? 'large' : 'small';
  const tooltipClass = `floating-tooltip ${size}`;

  useEffect(() => {
    if (triggerRef.current && tooltipRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      
      // Calculate available space in each direction
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      
      const spaceRight = viewportWidth - triggerRect.right;
      const spaceLeft = triggerRect.left;
      const spaceAbove = triggerRect.top;
      const spaceBelow = viewportHeight - triggerRect.bottom;
      
      let finalPosition = position;
      
      // Determine best position if we need to adapt
      if (position === 'right' && spaceRight < tooltipRect.width + 20) {
        if (spaceLeft > tooltipRect.width + 20) finalPosition = 'left';
        else if (spaceBelow > tooltipRect.height + 20) finalPosition = 'bottom';
        else if (spaceAbove > tooltipRect.height + 20) finalPosition = 'top';
      } else if (position === 'left' && spaceLeft < tooltipRect.width + 20) {
        if (spaceRight > tooltipRect.width + 20) finalPosition = 'right';
        else if (spaceBelow > tooltipRect.height + 20) finalPosition = 'bottom';
        else if (spaceAbove > tooltipRect.height + 20) finalPosition = 'top';
      } else if (position === 'top' && spaceAbove < tooltipRect.height + 20) {
        if (spaceBelow > tooltipRect.height + 20) finalPosition = 'bottom';
        else if (spaceRight > tooltipRect.width + 20) finalPosition = 'right';
        else if (spaceLeft > tooltipRect.width + 20) finalPosition = 'left';
      } else if (position === 'bottom' && spaceBelow < tooltipRect.height + 20) {
        if (spaceAbove > tooltipRect.height + 20) finalPosition = 'top';
        else if (spaceRight > tooltipRect.width + 20) finalPosition = 'right';
        else if (spaceLeft > tooltipRect.width + 20) finalPosition = 'left';
      }
      
      let style = {
        position: 'fixed',
        zIndex: 9999,
        opacity: isVisible ? 1 : 0,
        visibility: isVisible ? 'visible' : 'hidden',
        transition: 'opacity 0.2s ease-in-out, visibility 0.2s ease-in-out'
      };
      
      switch (finalPosition) {
        case 'right':
          style.left = triggerRect.right + 10;
          style.top = triggerRect.top + (triggerRect.height / 2) - (tooltipRect.height / 2);
          break;
        case 'left':
          style.left = triggerRect.left - tooltipRect.width - 10;
          style.top = triggerRect.top + (triggerRect.height / 2) - (tooltipRect.height / 2);
          break;
        case 'top':
          style.left = triggerRect.left + (triggerRect.width / 2) - (tooltipRect.width / 2);
          style.top = triggerRect.top - tooltipRect.height - 10;
          break;
        case 'bottom':
          style.left = triggerRect.left + (triggerRect.width / 2) - (tooltipRect.width / 2);
          style.top = triggerRect.bottom + 10;
          break;
      }
      
      // Boundary adjustments
      if (style.left < 10) style.left = 10;
      if (style.left + tooltipRect.width > viewportWidth - 10) {
        style.left = viewportWidth - tooltipRect.width - 10;
      }
      
      if (style.top < 10) style.top = 10;
      if (style.top + tooltipRect.height > viewportHeight - 10) {
        style.top = viewportHeight - tooltipRect.height - 10;
      }
      
      setTooltipStyle(style);
    }
  }, [isVisible, position, description]);

  const handleMouseEnter = () => setIsVisible(true);
  const handleMouseLeave = () => setIsVisible(false);

  const iconContent = isCustom ? (
    content
  ) : (
    <span className="ms-2 fs-6 d-flex rounded-circle align-items-center justify-content-center form-tooltip">
      {content}
    </span>
  );

  // Always render the tooltip, but control visibility with CSS
  const tooltipPortal = ReactDOM.createPortal(
    <div 
      className={`${tooltipClass}`} 
      style={tooltipStyle}
      ref={tooltipRef}
    >
      {description}
    </div>,
    document.body
  );

  return (
    <div className="tooltip-wrapper">
      <div
        ref={triggerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={handleMouseEnter}
        onBlur={handleMouseLeave}
      >
        {iconContent}
      </div>
      {tooltipPortal}
    </div>
  );
};

export default Tooltip;